import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Target, Lightbulb, TrendingUp } from "lucide-react";

export default function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  const features = [
    {
      icon: <Lightbulb className="w-5 h-5" />,
      title: "Creatividad",
      description: "Ideas frescas y originales",
    },
    {
      icon: <Target className="w-5 h-5" />,
      title: "Estrategia",
      description: "Enfoque orientado a objetivos",
    },
    {
      icon: <TrendingUp className="w-5 h-5" />,
      title: "Resultados",
      description: "Métricas que importan",
    },
  ];

  return (
    <section
      id="about"
      ref={ref}
      className="py-24 lg:py-32 bg-[#050505] relative overflow-hidden"
      data-testid="about-section"
    >
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-[#0a0a0a] -skew-x-12 origin-top-right hidden lg:block" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#D4AF37]/20 to-[#FF6B6B]/20 rounded-lg blur-xl" />
              <img
                src="https://images.unsplash.com/photo-1604611714877-a8108b03132f?crop=entropy&cs=srgb&fm=jpg&w=700&q=80"
                alt="Producciones Palacios workspace"
                className="relative rounded-lg w-full h-[300px] lg:h-[500px] object-cover border border-white/10"
                loading="lazy"
              />
            </div>

            {/* Experience Badge */}
            <div className="absolute -bottom-4 right-4 lg:bottom-8 lg:-right-8 bg-[#D4AF37] text-[#050505] p-4 lg:p-6 rounded-lg">
              <p className="font-playfair text-3xl lg:text-4xl font-bold">3+</p>
              <p className="text-xs lg:text-sm font-semibold">Años de</p>
              <p className="text-xs lg:text-sm font-semibold">Experiencia</p>
            </div>
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <p className="overline mb-4">Sobre Nosotros</p>
            
            <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Más que diseño,{" "}
              <span className="italic text-[#D4AF37]">creamos presencia</span>
            </h2>

            <p className="text-[#A3A3A3] leading-relaxed mb-6">
              <span className="text-white font-semibold">Producciones Palacios</span> es 
              un estudio creativo especializado en impulsar marcas y negocios a través 
              de soluciones digitales integrales. No solo diseñamos o publicamos contenido; 
              creamos experiencias visuales que conectan, posicionan y generan resultados.
            </p>

            <p className="text-[#A3A3A3] leading-relaxed mb-10">
              Nuestro enfoque combina creatividad estratégica con una ejecución impecable. 
              Cada proyecto es único, y trabajamos de cerca con cada cliente para entender 
              sus objetivos y superar sus expectativas.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-3 gap-3 lg:gap-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="text-center p-3 lg:p-4 bg-[#121212] rounded-lg border border-white/5"
                >
                  <div className="w-10 h-10 mx-auto mb-3 bg-[#D4AF37]/10 rounded-full flex items-center justify-center text-[#D4AF37]">
                    {feature.icon}
                  </div>
                  <p className="text-white font-semibold text-xs lg:text-sm mb-1">{feature.title}</p>
                  <p className="text-[#A3A3A3] text-xs hidden lg:block">{feature.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
